// src/lib/auth.ts

// 단일 헬퍼: API_BASE 합치기 + 토큰 자동 첨부 + 401 처리
const RAW_BASE = (import.meta.env.VITE_API_BASE || "").trim();

export const LS_TOKEN = "token";
export const SS_TOKEN = "token_session";

export type PersistMode = "local" | "session";

// 예) VITE_API_BASE = "https://juluy.cafe24.com"
// 절대금지: 뒤에 /api 붙이지 마세요! (라우터에서 이미 /api 사용)
function joinUrl(base: string, path: string) {
  const b = base.replace(/\/+$/, "");
  const p = path.replace(/^\/+/, "");
  return b ? `${b}/${p}` : `/${p}`;
}

export function getToken() {
  return localStorage.getItem(LS_TOKEN) || sessionStorage.getItem(SS_TOKEN) || "";
}

export function setToken(token: string, mode: PersistMode = "local") {
  if (mode === "local") {
    localStorage.setItem(LS_TOKEN, token);
    sessionStorage.removeItem(SS_TOKEN);
  } else {
    sessionStorage.setItem(SS_TOKEN, token);
    localStorage.removeItem(LS_TOKEN);
  }
}

export function clearToken() {
  localStorage.removeItem(LS_TOKEN);
  sessionStorage.removeItem(SS_TOKEN);
}

// 공용 fetch: path에는 **반드시** '/api/...' 형태로 넘기세요.
export async function authFetch(input: string, init: RequestInit = {}) {
  // input이 절대 URL이면 그대로 사용, 아니면 BASE와 합치기
  const isAbs = /^https?:\/\//i.test(input);
  const url = isAbs ? input : joinUrl(RAW_BASE, input.replace(/^\/+/, "/"));

  const headers = new Headers(init.headers || {});
  const t = getToken();
  if (t && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${t}`);
  }

  // FormData면 Content-Type을 직접 세팅하지 않음
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (isFormData) headers.delete("Content-Type");

  const res = await fetch(url, {
    ...init,
    headers,
    credentials: "include",
  });

  // 공통 401 처리: 로그인 만료 메시지 + 토큰 정리
  if (res.status === 401) {
    try {
      const ct = res.headers.get("content-type") || "";
      clearToken();

      if (ct.includes("application/json")) {
        const j = await res.clone().json().catch(() => null);
        const payload =
          j && typeof j === "object"
            ? { ...j, message: "로그인이 만료되었습니다." }
            : { message: "로그인이 만료되었습니다." };
        const nextHeaders = new Headers(res.headers);
        nextHeaders.set("content-type", "application/json");
        return new Response(JSON.stringify(payload), {
          status: res.status,
          statusText: res.statusText,
          headers: nextHeaders,
        });
      }

      const nextHeaders = new Headers(res.headers);
      nextHeaders.set("content-type", "text/plain; charset=utf-8");
      return new Response("로그인이 만료되었습니다.", {
        status: res.status,
        statusText: res.statusText,
        headers: nextHeaders,
      });
    } catch {
      clearToken();
    }
  }

  return res;
}

// 간단 JSON 헬퍼 (선택)
export async function authJson<T = any>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const r = await authFetch(path, init);
  const j = (await r.json().catch(() => null)) as T;
  return j;
}

/**
 * 1원 계좌 인증 시작
 * POST /api/ekyc/account/verify
 */
export async function startOneWonVerify(payload: {
  bank_code: string;
  account_no: string;
  holder_name: string;
}) {
  const res = await authFetch("/api/ekyc/account/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.message || "1원 인증 시작에 실패했습니다.");
  }
  return json;
}

/**
 * 1원 계좌 인증 확인
 * POST /api/ekyc/account/confirm
 */
export async function confirmOneWonVerify(code: string) {
  const res = await authFetch("/api/ekyc/account/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });

  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.message || "1원 인증 확인에 실패했습니다.");
  }
  return json;
}

export const API_BASE = RAW_BASE;
